// src/components/charts/ServiceDistributionChart.jsx
import React, { useState, useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const ServiceDistributionChart = ({ data = [], loading = false, error = null }) => {
  const [viewMode, setViewMode] = useState('absolute'); // 'absolute' or 'percentage'
  const [hiddenServices, setHiddenServices] = useState(new Set());

  // Transform data for stacked area chart
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    // Group data by month
    const monthlyData = {};
    
    data.forEach(row => {
      const month = row.month_label; // YYYY-MM format
      if (!monthlyData[month]) {
        monthlyData[month] = { month };
      }
      
      const value = viewMode === 'percentage' ? 
        parseFloat(row.percentage) : 
        parseInt(row.appointment_count);
        
      monthlyData[month][row.service_name] = value;
    });

    return Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month));
  }, [data, viewMode]);

  // Get unique services for color mapping
  const services = useMemo(() => {
    const serviceSet = new Set();
    data.forEach(row => serviceSet.add(row.service_name));
    return Array.from(serviceSet).sort();
  }, [data]);

  // Color palette for services
  const serviceColors = useMemo(() => {
    const colors = [
      '#8B5CF6', '#06B6D4', '#10B981', '#F59E0B', '#EF4444',
      '#EC4899', '#84CC16', '#6366F1', '#F97316', '#14B8A6',
      '#8B5A2B', '#7C3AED', '#059669', '#DC2626', '#9333EA'
    ];
    
    const colorMap = {};
    services.forEach((service, index) => {
      colorMap[service] = colors[index % colors.length];
    });
    return colorMap;
  }, [services]);

  // Filter visible services
  const visibleServices = services.filter(service => !hiddenServices.has(service));

  // Toggle service visibility
  const toggleService = (service) => {
    const newHidden = new Set(hiddenServices);
    if (newHidden.has(service)) {
      newHidden.delete(service);
    } else {
      newHidden.add(service);
    }
    setHiddenServices(newHidden);
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;

    const total = payload.reduce((sum, item) => sum + (item.value || 0), 0);

    return (
      <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-lg p-3 shadow-xl">
        <p className="font-semibold text-gray-900 mb-2">{label}</p>
        {payload
          .sort((a, b) => (b.value || 0) - (a.value || 0))
          .map((item, index) => (
            <div key={index} className="flex items-center justify-between gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-sm"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-gray-700">{item.dataKey}</span>
              </div>
              <div className="text-right">
                <span className="font-medium text-gray-900">
                  {viewMode === 'percentage' ? 
                    `${item.value?.toFixed(1)}%` : 
                    item.value
                  }
                </span>
                {viewMode === 'absolute' && total > 0 && (
                  <div className="text-xs text-gray-500">
                    {((item.value / total) * 100).toFixed(1)}%
                  </div>
                )}
              </div>
            </div>
          ))}
        {viewMode === 'absolute' && (
          <div className="border-t border-gray-200 mt-2 pt-2">
            <div className="flex justify-between text-sm font-medium">
              <span>Total:</span>
              <span>{total}</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="w-full h-96 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 border-3 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
          <span className="text-gray-600">טוען התפלגות שירותים...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-96 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-lg mb-2">⚠️</div>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <div className="w-full h-96 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 text-4xl mb-4">📊</div>
          <p className="text-gray-600">אין מידע זמין כעת</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        {/* View Mode Toggle */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">תצוגה:</span>
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('absolute')}
              className={`px-3 py-1 text-sm rounded-md transition-all ${
                viewMode === 'absolute'
                  ? 'bg-white text-purple-600 shadow-sm font-medium'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              מספר
            </button>
            <button
              onClick={() => setViewMode('percentage')}
              className={`px-3 py-1 text-sm rounded-md transition-all ${
                viewMode === 'percentage'
                  ? 'bg-white text-purple-600 shadow-sm font-medium'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              אחוזים
            </button>
          </div>
        </div>

        {/* Service Legend */}
        <div className="flex flex-wrap gap-2">
          {services.map(service => (
            <button
              key={service}
              onClick={() => toggleService(service)}
              className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm transition-all ${
                hiddenServices.has(service)
                  ? 'bg-gray-100 text-gray-400'
                  : 'bg-white border border-gray-200 text-gray-700 hover:border-purple-300'
              }`}
            >
              <div
                className={`w-3 h-3 rounded-sm ${
                  hiddenServices.has(service) ? 'opacity-30' : ''
                }`}
                style={{ backgroundColor: serviceColors[service] }}
              />
              <span className={hiddenServices.has(service) ? 'line-through' : ''}>
                {service}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white/50 backdrop-blur-sm rounded-xl border border-gray-200/50 p-6">
        <ResponsiveContainer width="100%" height={400}>
          <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <defs>
              {visibleServices.map(service => (
                <linearGradient key={service} id={`gradient-${service.replace(/\s+/g, '-')}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={serviceColors[service]} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={serviceColors[service]} stopOpacity={0.1}/>
                </linearGradient>
              ))}
            </defs>
            
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />
            <XAxis 
              dataKey="month" 
              stroke="#6b7280"
              fontSize={12}
              tickMargin={8}
            />
            <YAxis 
              stroke="#6b7280"
              fontSize={12}
              tickMargin={8}
              label={{ 
                value: viewMode === 'percentage' ? 'Percentage (%)' : 'Appointments', 
                angle: -90, 
                position: 'insideLeft',
                style: { textAnchor: 'middle' }
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            
            {visibleServices.map(service => (
              <Area
                key={service}
                type="monotone"
                dataKey={service}
                stackId="1"
                stroke={serviceColors[service]}
                fill={`url(#gradient-${service.replace(/\s+/g, '-')})`}
                strokeWidth={2}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ServiceDistributionChart;